import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Blocks, Check, ChevronDown, ChevronRight, Copy, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, copyText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useW } from "./ctx";

/* Workspace preferences live in this browser only. */
const SETTINGS_KEY = "condoor.settings";
export const DEFAULT_SETTINGS = { chatOpen: true, showMetLines: false, startMode: "quick" };

export function readSettings() {
  try {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}") };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function useSettings() {
  const [s, setS] = useState(readSettings);
  const set = useCallback((patch) => setS((cur) => {
    const next = { ...cur, ...patch };
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* private window: keep it in memory */ }
    return next;
  }), []);
  return [s, set];
}

function Panel({ title, children, testId }) {
  return (
    <aside className="scroll flex w-[272px] shrink-0 flex-col bg-[var(--panel)] pb-4" data-testid={testId}>
      <p className="panel-title px-4 pb-2 pt-4">{title}</p>
      {children}
    </aside>
  );
}

export function ExtensionsPanel() {
  return (
    <Panel title="Extensions" testId="panel-extensions">
      <div className="mx-4 mt-2 rounded-lg border border-dashed border-[var(--line2)] px-4 py-8 text-center text-[12.5px] text-[var(--ink2)]">
        <Blocks size={20} className="mx-auto mb-2 text-[var(--muted)]" />
        No extensions yet.
      </div>
    </Panel>
  );
}

export function ProfilePanel() {
  const w = useW();
  const { me, signOut } = useAuth();
  const nav = useNavigate();
  const p = w.project;
  if (w.role === "architect") {
    return (
      <Panel title="Profile" testId="panel-profile">
        <div className="px-4 text-[13px]">
          <p className="font-semibold">Architect</p>
          <p className="text-[var(--ink2)]">Reviewing by invite from {p.supplier_name}. No account needed; this link only opens this project.</p>
        </div>
      </Panel>
    );
  }
  return (
    <Panel title="Profile" testId="panel-profile">
      <div className="space-y-3 px-4 text-[13px]">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--ink)] text-[13px] font-semibold text-white">
            {(me?.user?.name || "?").split(" ").map((x) => x[0]).join("").slice(0, 2)}</span>
          <div className="min-w-0">
            <p className="truncate font-semibold">{me?.user?.name}</p>
            <p className="truncate text-[var(--ink2)]">{me?.orgs?.[0]?.name}</p>
          </div>
        </div>
        {me?.demo && <span className="badge b-met">Demo workspace · synthetic data</span>}
        <div className="flex flex-col gap-1.5 pt-2">
          <button className="btn btn-sm justify-start" onClick={() => nav("/projects")}>All projects</button>
          <button className="btn btn-sm justify-start" onClick={async () => { await signOut(); nav("/"); }} data-testid="profile-sign-out">Sign out</button>
        </div>
      </div>
    </Panel>
  );
}

export function SettingsPanel({ settings, setSettings }) {
  const Row = ({ k, label, hint }) => (
    <label className="flex cursor-pointer items-start gap-3 px-4 py-2 hover:bg-black/5">
      <input type="checkbox" className="mt-0.5" checked={!!settings[k]} onChange={(e) => setSettings({ [k]: e.target.checked })} data-testid={`setting-${k}`} />
      <span><span className="block text-[13px] font-medium">{label}</span><span className="block text-[12px] text-[var(--muted)]">{hint}</span></span>
    </label>
  );
  return (
    <Panel title="Settings" testId="panel-settings">
      <Row k="chatOpen" label="Open the chat by default" hint="The chat panel starts open when you open a project." />
      <Row k="showMetLines" label="Show met lines on flagged doors" hint="Otherwise only the lines that need a decision show." />
      <div className="px-4 py-2">
        <p className="text-[13px] font-medium">Open files in</p>
        <div className="mt-1.5 flex rounded-full border border-[var(--line2)] bg-white p-0.5 text-[12px]">
          {[["quick", "Quick view"], ["doc", "Document"]].map(([v, l]) => (
            <button key={v} className={`flex-1 rounded-full px-3 py-1 ${settings.startMode === v ? "bg-[var(--ink)] text-white" : ""}`}
              onClick={() => setSettings({ startMode: v })} data-testid={`setting-start-${v}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="px-4 pt-3 text-[11.5px] text-[var(--muted)]">Saved in this browser.</p>
    </Panel>
  );
}

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");

export function DebugPanel() {
  const w = useW();
  const p = w.project;
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState({ calls: true, trace: false });
  const load = useCallback(() => api.get(`/condoor/projects/${p.id}/debug`).then((r) => { setD(r.data); setErr(""); }).catch((e) => setErr(String(e?.message))), [p.id]);
  useEffect(() => { load(); }, [load, p.run.status, p.run.finished_at]);
  const run = d?.run || p.run;
  const dbg = run.debug;
  const total = (d?.calls || []).reduce((n, c) => n + (c.ms || 0), 0);
  const copy = async () => { await copyText(JSON.stringify(d, null, 2)); toast.success("Debug log copied"); };
  return (
    <aside className="scroll flex w-[380px] shrink-0 flex-col bg-[var(--panel)] pb-4 text-[12.5px]" data-testid="panel-debug">
      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <p className="panel-title">Debug</p>
        <div className="flex gap-1">
          <button className="icon-btn" onClick={load} aria-label="Refresh"><RefreshCw size={13} /></button>
          <button className="icon-btn" onClick={copy} disabled={!d} aria-label="Copy the debug log"><Copy size={13} /></button>
        </div>
      </div>
      {err && <p className="px-4 text-[var(--red)]">{err}</p>}
      <div className="mx-4 rounded-lg border border-[var(--line2)] bg-white p-3">
        <div className="flex items-center gap-2">
          <span className={`badge ${run.status === "failed" ? "b-conflict" : run.status === "running" ? "b-arch" : "b-resolved"}`}>{run.status}</span>
          <span className="text-[var(--muted)]">{fmtTime(run.started_at)} → {fmtTime(run.finished_at)}</span>
        </div>
        {run.error && <p className="mt-2 font-medium text-[var(--red)]" data-testid="debug-error">{run.error}</p>}
        {dbg && (
          <div className="mt-2 space-y-1">
            <p><span className="text-[var(--muted)]">Step:</span> <span className="mono">{dbg.step || "—"}</span></p>
            <p><span className="text-[var(--muted)]">Exception:</span> <span className="mono">{dbg.type}</span></p>
            <p className="break-words"><span className="text-[var(--muted)]">Message:</span> {dbg.message}</p>
          </div>
        )}
      </div>

      <Section title="Steps">
        {run.steps.map((s) => (
          <div key={s.key} className="flex items-start gap-2 py-1">
            {s.status === "done" ? <Check size={13} className="mt-0.5 text-[var(--green)]" /> : s.status === "failed" ? <X size={13} className="mt-0.5 text-[var(--red)]" />
              : <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--muted)]" />}
            <span className="min-w-0"><span className="font-medium">{s.title}</span>
              {s.findings?.length ? <span className="block text-[var(--muted)]">{s.findings.length} finding{s.findings.length > 1 ? "s" : ""}</span> : null}</span>
          </div>
        ))}
      </Section>

      <Section title="Files">
        {(d?.files || p.files).map((f) => (
          <div key={f.role} className="py-1">
            <p className="font-medium">{f.label} <span className="font-normal text-[var(--muted)]">· read as {f.role}{f.detected ? ` (detected: ${f.detected.replace("_", " ")})` : ""}</span></p>
            <p className="truncate text-[var(--muted)]">{f.name}{f.sample ? " · sample" : " · uploaded"}</p>
          </div>
        ))}
      </Section>

      <Collapsible title={`Agent calls (${d?.calls?.length || 0}) · ${(total / 1000).toFixed(1)} s`} open={open.calls} onToggle={() => setOpen((o) => ({ ...o, calls: !o.calls }))}>
        {(d?.calls || []).length === 0 && <p className="py-1 text-[var(--muted)]">No agent calls recorded yet.</p>}
        {(d?.calls || []).map((c, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto] gap-x-2 border-t border-[var(--line)] py-1.5 first:border-0" data-testid="debug-call">
            <span className="mono truncate">{c.agent}</span>
            <span className="tnum text-right">{c.ms} ms</span>
            <span className="text-[var(--muted)]">{c.model}{c.repaired ? " · repaired once" : ""}</span>
            <span className={`text-right ${c.cached ? "text-[var(--muted)]" : "text-[var(--accent-ink)]"}`}>
              {c.cached ? "cached" : `live${c.usage?.[0]?.total_tokens ? ` · ${c.usage.reduce((n, u) => n + (u.total_tokens || 0), 0)} tok` : ""}`}</span>
          </div>
        ))}
      </Collapsible>

      {dbg?.traceback && (
        <Collapsible title="Traceback" open={open.trace} onToggle={() => setOpen((o) => ({ ...o, trace: !o.trace }))}>
          <pre className="mono max-h-[320px] overflow-auto whitespace-pre-wrap break-words rounded-md bg-[var(--ink)] p-2 text-[10.5px] leading-snug text-white">{dbg.traceback}</pre>
        </Collapsible>
      )}
    </aside>
  );
}

function Section({ title, children }) {
  return <div className="mx-4 mt-4"><p className="panel-title mb-1">{title}</p>{children}</div>;
}

function Collapsible({ title, open, onToggle, children }) {
  return (
    <div className="mx-4 mt-4">
      <button className="panel-title mb-1 flex items-center gap-1" onClick={onToggle}>{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}{title}</button>
      {open && children}
    </div>
  );
}
